import { useState } from 'react';
import { useDebounce } from 'react-use';
import styled from 'styled-components';

import type { Skin } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { CardsMissing } from '@sorare/core/src/atoms/icons/CardsMissing.tsx';
import { iconSearch } from '@sorare/core/src/atoms/icons/system/iconSearch.ts';
import { SearchInput } from '@sorare/core/src/atoms/inputs/SearchInput';
import { Switch } from '@sorare/core/src/atoms/inputs/Switch';
import { Horizontal, SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { VerticalDivider } from '@sorare/core/src/atoms/ui/Divider/index.tsx';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery.ts';

import { SkinFilter } from '#src/components/myCards/SkinFilter/index.tsx';
import { useSetHasMultipleSkins } from '#src/hooks/my-cards/useSetHasMultipleSkins.ts';

const SearchWrapper = styled(Horizontal)`
  flex: 1;
`;

const SkinFilterWrapper = styled(Horizontal)`
  flex: 1;
  @media ${laptopAndAbove} {
    flex: unset;
  }
`;

const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: var(--half-unit);
  cursor: pointer;
  white-space: nowrap;
`;

const TextSearchInput = ({
  onChange,
  value: initialValue,
  placeholder,
  autoFocus,
}: {
  onChange: (value: string) => void;
  value: string;
  placeholder: string;
  autoFocus?: boolean;
}) => {
  const [value, setValue] = useState(initialValue);
  useDebounce(
    () => {
      onChange(value);
    },
    300,
    [value]
  );

  return (
    <SearchInput
      fullWidth
      small
      rounded
      withIcon
      value={value}
      autoFocus={autoFocus}
      onChange={event => {
        setValue(event.target.value);
      }}
      placeholder={placeholder}
    />
  );
};

type Props = {
  search: string;
  onSearchChange: (value: string) => void;
  skin: Skin | undefined;
  onSkinChange: (skin: Skin | undefined) => void;
  setSlug: string;
  placeholder: string;
  showAll: boolean;
  onShowAllChange: (showAll: boolean) => void;
};

const CollectionFiltersComponent = ({
  search,
  onSearchChange,
  skin,
  onSkinChange,
  setSlug,
  placeholder,
  showAll,
  onShowAllChange,
}: Props) => {
  const isLaptop = useIsLaptop();
  const setHasMultipleSkins = useSetHasMultipleSkins(setSlug);
  const minimizable = !isLaptop && setHasMultipleSkins;
  const [minimizeSearch, setMinimizeSearch] = useState(minimizable);

  return (
    <SBHorizontal center>
      <ToggleLabel>
        <CardsMissing size={2} />
        <Switch
          checked={showAll}
          onChange={e => onShowAllChange(e.target.checked)}
        />
      </ToggleLabel>
      <VerticalDivider height={24} />
      {!!setHasMultipleSkins && (!minimizable || !!minimizeSearch) && (
        <>
          <SkinFilterWrapper center>
            <SkinFilter
              skin={skin}
              onSkinChange={onSkinChange}
              setSlug={setSlug}
            />
          </SkinFilterWrapper>
          <VerticalDivider height={24} />
        </>
      )}
      {minimizeSearch ? (
        <Badge color="grey" badgeContent={search.trim() ? 1 : 0}>
          <IconButton
            icon={iconSearch}
            size="small"
            color="quaternary"
            onClick={() => setMinimizeSearch(false)}
          />
        </Badge>
      ) : (
        <SearchWrapper
          onBlur={e => {
            if (minimizable && !e.currentTarget.contains(e.relatedTarget)) {
              setMinimizeSearch(true);
            }
          }}
        >
          <TextSearchInput
            onChange={onSearchChange}
            value={search}
            autoFocus={minimizable}
            placeholder={placeholder}
          />
        </SearchWrapper>
      )}
    </SBHorizontal>
  );
};

export const CollectionFilters = (props: Props) => {
  const { setSlug } = props;
  return <CollectionFiltersComponent key={setSlug} {...props} />;
};
